/**
 * Health Data Report Page
 *
 * Coverage and quality metrics for the health_data collection.
 * Summary cards, coverage distribution, growth over time, data freshness,
 * source breakdown, and metric breakdown.
 */

import { useState, useEffect, useCallback } from 'react'
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts'
import { healthDataAPI } from '../services/api'
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
  Button
} from '../components/ui'
import {
  RefreshCw, Loader2, AlertCircle, Heart, Users, Database, Clock, Activity, UserX, ChevronDown
} from 'lucide-react'

function formatNumber(value) {
  if (value === null || value === undefined) return '-'
  return new Intl.NumberFormat('en-US').format(value)
}

export default function HealthDataPage() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [gaps, setGaps] = useState(null)
  const [gapsLoading, setGapsLoading] = useState(false)
  const [gapsError, setGapsError] = useState(null)

  const fetchData = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await healthDataAPI.getReport()
      if (response.data.success) {
        setData(response.data.data)
      }
    } catch (err) {
      console.error('[HEALTH_DATA] Error:', err)
      setError(err.response?.data?.error?.message || err.message)
    } finally {
      setLoading(false)
    }
  }, [])

  const fetchGaps = useCallback(async (offset = 0) => {
    try {
      setGapsLoading(true)
      setGapsError(null)
      const response = await healthDataAPI.getGaps({ limit: 100, offset })
      if (response.data.success) {
        const result = response.data.data
        if (offset === 0) {
          setGaps(result)
        } else {
          // Append to existing
          setGaps(prev => ({
            ...result,
            patients: [...(prev?.patients || []), ...result.patients],
          }))
        }
      }
    } catch (err) {
      console.error('[HEALTH_DATA] Gaps error:', err)
      setGapsError(err.response?.data?.error?.message || err.message)
    } finally {
      setGapsLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const summary = data?.summary
  const coverage = data?.coverage
  const coveragePct = coverage?.with_data != null && coverage?.total_order_patients
    ? Math.round(coverage.with_data / coverage.total_order_patients * 1000) / 10
    : 0

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Health Data</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Patient health data coverage, quality, and freshness metrics
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchData}
            disabled={loading}
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            <span className="ml-1.5">Refresh</span>
          </Button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <Card>
          <CardContent className="py-6">
            <div className="text-center">
              <AlertCircle className="h-8 w-8 text-destructive mx-auto mb-2" />
              <p className="text-destructive">{error}</p>
              <Button variant="outline" onClick={fetchData} className="mt-3">
                Try Again
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Loading */}
      {loading && (
        <Card>
          <CardContent className="py-12">
            <div className="text-center text-muted-foreground">
              <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" />
              <p>Loading health data report...</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary Cards */}
      {!loading && !error && summary && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            <Card>
              <CardContent className="py-4 px-4">
                <div className="flex items-center gap-2 mb-1">
                  <Database className="h-4 w-4 text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">Total Records</span>
                </div>
                <p className="text-2xl font-bold">{formatNumber(summary.total_records)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="py-4 px-4">
                <div className="flex items-center gap-2 mb-1">
                  <Users className="h-4 w-4 text-blue-500" />
                  <span className="text-xs text-muted-foreground">Patients with Data</span>
                </div>
                <p className="text-2xl font-bold text-blue-600">{formatNumber(coverage?.with_data)}</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  of {formatNumber(coverage?.total_order_patients)} order patients
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="py-4 px-4">
                <div className="flex items-center gap-2 mb-1">
                  <Heart className="h-4 w-4 text-green-500" />
                  <span className="text-xs text-muted-foreground">Coverage</span>
                </div>
                <p className="text-2xl font-bold text-green-600">{coveragePct}%</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="py-4 px-4">
                <div className="flex items-center gap-2 mb-1">
                  <UserX className="h-4 w-4 text-red-500" />
                  <span className="text-xs text-muted-foreground">No Data</span>
                </div>
                <p className="text-2xl font-bold text-red-500">{formatNumber(coverage?.without_data)}</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  order patients, 0 data pts
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="py-4 px-4">
                <div className="flex items-center gap-2 mb-1">
                  <Clock className="h-4 w-4 text-amber-500" />
                  <span className="text-xs text-muted-foreground">Avg Data Age</span>
                </div>
                <p className="text-2xl font-bold">{data?.freshness?.avg_age_days}d</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  median {data?.freshness?.median_age_days}d
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="py-4 px-4">
                <div className="flex items-center gap-2 mb-1">
                  <Activity className="h-4 w-4 text-purple-500" />
                  <span className="text-xs text-muted-foreground">Distinct Metrics</span>
                </div>
                <p className="text-2xl font-bold text-purple-600">{formatNumber(summary.distinct_metrics)}</p>
              </CardContent>
            </Card>
          </div>

          {/* Date range */}
          {summary.earliest && summary.latest && (
            <p className="text-xs text-muted-foreground">
              Data spans {new Date(summary.earliest).toLocaleDateString()} to {new Date(summary.latest).toLocaleDateString()}
            </p>
          )}
        </>
      )}

      {/* Coverage Distribution */}
      {!loading && !error && coverage?.distribution?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Coverage Distribution</CardTitle>
            <CardDescription>
              Patient count by number of health data points per patient.
              {coverage.without_data > 0 && ` ${formatNumber(coverage.without_data)} order patients have no health data.`}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={coverage.distribution}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey="bucket" fontSize={12} />
                <YAxis allowDecimals={false} tickFormatter={(v) => formatNumber(v)} />
                <Tooltip
                  contentStyle={{ backgroundColor: 'hsl(var(--popover))', color: 'hsl(var(--popover-foreground))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }}
                  formatter={(value) => [formatNumber(value), 'Patients']}
                />
                <Bar dataKey="patients" fill="#3B82F6" name="Patients" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Growth Over Time */}
      {!loading && !error && data?.growth?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Monthly Growth</CardTitle>
            <CardDescription>
              Health data records and distinct patients per month
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={350}>
              <LineChart data={data.growth}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis
                  dataKey="month"
                  fontSize={12}
                  tickFormatter={(m) => {
                    const [y, mo] = m.split('-')
                    return `${mo}/${y.slice(2)}`
                  }}
                  interval="preserveStartEnd"
                />
                <YAxis yAxisId="left" tickFormatter={(v) => formatNumber(v)} />
                <YAxis yAxisId="right" orientation="right" tickFormatter={(v) => formatNumber(v)} />
                <Tooltip
                  contentStyle={{ backgroundColor: 'hsl(var(--popover))', color: 'hsl(var(--popover-foreground))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }}
                  formatter={(value, name) => [formatNumber(value), name === 'records' ? 'Records' : 'Patients']}
                />
                <Legend />
                <Line yAxisId="left" type="monotone" dataKey="records" stroke="#3B82F6" strokeWidth={2} dot={false} name="Records" />
                <Line yAxisId="right" type="monotone" dataKey="patients" stroke="#22C55E" strokeWidth={2} dot={false} name="Patients" />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Data Freshness */}
      {!loading && !error && data?.freshness?.distribution?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Data Freshness</CardTitle>
            <CardDescription>
              Patient count by age of most recent health data point.
              Avg: {data.freshness.avg_age_days}d, Median: {data.freshness.median_age_days}d
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={data.freshness.distribution}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey="bucket" fontSize={12} />
                <YAxis allowDecimals={false} tickFormatter={(v) => formatNumber(v)} />
                <Tooltip
                  contentStyle={{ backgroundColor: 'hsl(var(--popover))', color: 'hsl(var(--popover-foreground))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }}
                  formatter={(value) => [formatNumber(value), 'Patients']}
                />
                <Bar
                  dataKey="patients"
                  name="Patients"
                  radius={[4, 4, 0, 0]}
                  fill="#F59E0B"
                />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* By Source Table */}
      {!loading && !error && data?.by_source?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">By Source</CardTitle>
            <CardDescription>
              Health data records and patients by intake source (top 20)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="border rounded-lg overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-3 font-medium">Source</th>
                    <th className="text-right p-3 font-medium">Records</th>
                    <th className="text-right p-3 font-medium">Patients</th>
                  </tr>
                </thead>
                <tbody>
                  {data.by_source.map((row) => (
                    <tr key={row.source} className="border-t hover:bg-muted/30">
                      <td className="p-3 font-medium text-sm">{row.source}</td>
                      <td className="p-3 text-right text-sm font-mono">{formatNumber(row.records)}</td>
                      <td className="p-3 text-right text-sm font-mono">{formatNumber(row.patients)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* By Metric Table */}
      {!loading && !error && data?.by_metric?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">By Metric</CardTitle>
            <CardDescription>
              Record count by health metric type (top 30)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="border rounded-lg overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-3 font-medium">Metric</th>
                    <th className="text-right p-3 font-medium">Count</th>
                  </tr>
                </thead>
                <tbody>
                  {data.by_metric.map((row) => (
                    <tr key={row.metric} className="border-t hover:bg-muted/30">
                      <td className="p-3 font-medium text-sm">{row.metric}</td>
                      <td className="p-3 text-right text-sm font-mono">{formatNumber(row.count)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Coverage Gaps: patients with 1 order, no health data */}
      {!loading && !error && summary && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  <UserX className="h-5 w-5 text-red-500" />
                  Coverage Gaps
                </CardTitle>
                <CardDescription>
                  Patients with exactly 1 order but zero health data points
                  {gaps && ` (${formatNumber(gaps.total)} total)`}
                </CardDescription>
              </div>
              {!gaps && !gapsLoading && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchGaps(0)}
                >
                  Load
                </Button>
              )}
            </div>
          </CardHeader>
          {gapsError && (
            <CardContent className="pt-0">
              <p className="text-sm text-destructive">{gapsError}</p>
            </CardContent>
          )}
          {gapsLoading && !gaps && (
            <CardContent className="py-6">
              <div className="text-center text-muted-foreground">
                <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
                <p className="text-sm">Loading gap patients... this may take a moment.</p>
              </div>
            </CardContent>
          )}
          {gaps?.patients?.length > 0 && (
            <CardContent>
              <div className="border rounded-lg overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left p-3 font-medium">Name</th>
                      <th className="text-left p-3 font-medium">Email</th>
                      <th className="text-left p-3 font-medium hidden sm:table-cell">Patient ID</th>
                      <th className="text-right p-3 font-medium hidden sm:table-cell">Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {gaps.patients.map((row) => (
                      <tr key={row.patient_id} className="border-t hover:bg-muted/30">
                        <td className="p-3 text-sm font-medium">
                          {row.first_name || row.last_name
                            ? `${row.first_name || ''} ${row.last_name || ''}`.trim()
                            : <span className="text-muted-foreground">-</span>}
                        </td>
                        <td className="p-3 text-sm text-muted-foreground">{row.email || '-'}</td>
                        <td className="p-3 text-sm font-mono text-muted-foreground hidden sm:table-cell">
                          {row.patient_id?.slice(-8)}
                        </td>
                        <td className="p-3 text-right text-sm text-muted-foreground hidden sm:table-cell">
                          {row.created_at ? new Date(row.created_at).toLocaleDateString() : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Load more */}
              {gaps.patients.length < gaps.total && (
                <div className="text-center mt-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fetchGaps(gaps.patients.length)}
                    disabled={gapsLoading}
                  >
                    {gapsLoading
                      ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                      : <ChevronDown className="h-4 w-4 mr-1.5" />}
                    Load more ({formatNumber(gaps.total - gaps.patients.length)} remaining)
                  </Button>
                </div>
              )}
            </CardContent>
          )}
          {gaps && gaps.total === 0 && (
            <CardContent>
              <p className="text-sm text-muted-foreground">No gap patients found — all single-order patients have health data.</p>
            </CardContent>
          )}
        </Card>
      )}

      {/* Empty state */}
      {!loading && !error && summary?.total_records === 0 && (
        <Card>
          <CardContent className="py-12">
            <div className="text-center text-muted-foreground">
              <p>No health data found for this clinic.</p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
